import { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { InputRef } from 'antd';
import { ColumnType, FilterConfirmProps, SortOrder } from 'antd/lib/table/interface';
import { CustomDeviceModel } from 'utils/globalModels';
import { Icon } from 'components/atoms/Icons';
import { COLORS } from 'constants/colors';
import { ColumnSearchTranslationsType, DeviceDataIndex, getColumnSearchProps } from 'components/organisms/PeripheralDevices/SearchPeripheralColumns';
import LinkButton from 'components/atoms/Button/LinkButton';
import { DeviceConnectivityType } from 'constants/devices';
import TextAtom from 'components/atoms/TextAtom';
import ContextualMenu from 'components/atoms/ContextualMenu';
import MenuAtom from 'components/atoms/MenuAtom';
import './styles.scss';

interface Props {
  columnSearchTranslations?: ColumnSearchTranslationsType;
  searchInput: RefObject<InputRef | null>;
  connectivityTagsManager: Record<DeviceConnectivityType, ReactNode>,
  onSearch?: (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DeviceDataIndex) => void;
  onReset?: (clearFilters: () => void, confirm: (param?: FilterConfirmProps) => void, dataIndex: DeviceDataIndex) => void;
  onSelectSharedGateway: (gatewayUuid: string) => void;
  onOpenMaintenanceActionsDrawer: (device: CustomDeviceModel) => void;
  onOpenUpdateDeviceDrawer: (device: CustomDeviceModel) => void;
  onOpenDeleteDeviceModal: (device: CustomDeviceModel) => void;
  onOpenAlertNotificationDrawer: (device: CustomDeviceModel) => void;
  onDeleteAlertNotificationDrawer: (device: CustomDeviceModel) => void;
}

interface SortPeripheralsByBatteryLevelProps {
  a: number | null,
  b: number | null,
  sortOrder?: SortOrder,
}

export const sortPeripheralsByBatteryLevel = ({ a, b, sortOrder }: SortPeripheralsByBatteryLevelProps) => {
  if (a === null) return sortOrder === 'ascend' ? 1 : -1;
  if (b === null) return sortOrder === 'ascend' ? -1 : 1;
  return a - b;
};

export const ColumnsPeripheralDevices = ({
  columnSearchTranslations,
  searchInput,
  connectivityTagsManager,
  onSearch,
  onReset,
  onSelectSharedGateway,
  onOpenDeleteDeviceModal,
  onOpenMaintenanceActionsDrawer,
  onOpenUpdateDeviceDrawer,
  onDeleteAlertNotificationDrawer,
  onOpenAlertNotificationDrawer,
}: Props): ColumnType<CustomDeviceModel>[] => {
  const { t } = useTranslation();

  return [
    {
      title: 'Id',
      key: 'deviceId',
      width: '10%',
      render: ({ deviceId, gatewayUuid }: CustomDeviceModel) => {
        return (
          <div className={'Columns__cell'}>
            <p className={'Columns__id'}>{deviceId.split(':')[0]}</p>
            {!!gatewayUuid && (<LinkButton className={'Columns__alias'} title={gatewayUuid || ''} onClick={() => onSelectSharedGateway(gatewayUuid)} />)}
          </div>);
      }
    },
    {
      title: `${t('type')}`,
      key: 'deviceType',
      width: '20%',
      filterIcon: (filtered: boolean) => (
        <Icon.Search style={{ color: filtered ? COLORS.corporate : undefined }} />
      ),
      ...getColumnSearchProps({ dataIndex: 'deviceType', onSearch, onReset, searchInput, translations: columnSearchTranslations }),
    },
    {
      title: `${t('name')}`,
      key: 'deviceName',
      width: '25%',
      filterIcon: (filtered: boolean) => (
        <Icon.Search style={{ color: filtered ? COLORS.corporate : undefined }} />
      ),
      ...getColumnSearchProps({ dataIndex: 'deviceName', onSearch, onReset, searchInput, translations: columnSearchTranslations }),
    },
    {
      title: `${t('remaining_battery')}`,
      key: 'batteryLevel',
      width: '15%',
      render: ({ batteryLevel }: CustomDeviceModel) => {
        return batteryLevel !== null
          ? <span>{`${batteryLevel}%`}</span>
          : <span className='Columns__notReportedText'>{t('not_reported')}</span>
      },
      sorter: (a, b, sortOrder) => sortPeripheralsByBatteryLevel({ a: a.batteryLevel, b: b.batteryLevel, sortOrder }),
    },
    {
      title: `${t('_ASSET_DEVICE_COLUMN_CONNECTIVITY_TITLE')}`,
      key: 'connectivity',
      width: '15%',
      render: ({ connectivity }) => <span>{connectivityTagsManager[connectivity as DeviceConnectivityType]}</span>,
    },
    {
      title: `${t('_ASSET_DEVICE_COLUMN_UPDATED_AT_TITLE')}`,
      key: 'updatedAtLiteral',
      width: '10%',
      render: ({ updatedAt, updatedAtLiteral }) => <TextAtom disabled={!updatedAt}>{updatedAtLiteral}</TextAtom>,
    },
    {
      title: t('actions'),
      key: 'menuExtra',
      width: '5%',
      render: (values: CustomDeviceModel) => {
        return <ContextualMenu menu={(
          <MenuAtom items={[
            {
              key: 'maintenanceActions',
              label: t('maintenance_actions'),
              disabled: !values.gatewayUuid,
              onClick: () => onOpenMaintenanceActionsDrawer(values),
            },
            values.allowAlertNotification && !values.alertNotificationConfig ? {
              key: 'createAlertNotification',
              label: t('_DEVICES_COLUMNS_CONTEXTUAL_MENU_CREATE_ALERT_NOTIFICATION_BUTTON_TEXT'),
              onClick: () => onOpenAlertNotificationDrawer(values),
            } : null,
            values.allowAlertNotification && values.alertNotificationConfig ? {
              key: 'updateAlertNotification',
              label: t('_DEVICES_COLUMNS_CONTEXTUAL_MENU_UPDATE_ALERT_NOTIFICATION_BUTTON_TEXT'),
              onClick: () => onOpenAlertNotificationDrawer(values),
            } : null,
            values.allowAlertNotification ? {
              key: 'deleteAlertNotification',
              label: t('_DEVICES_COLUMNS_CONTEXTUAL_MENU_DELETE_ALERT_NOTIFICATION_BUTTON_TEXT'),
              onClick: () => onDeleteAlertNotificationDrawer(values),
              disabled: !values.alertNotificationConfig,
              danger: true,
            } : null,
            !values.gatewayUuid ? {
              key: 'updateDevice',
              label: t('_UPDATE_DEVICE_WITHOUT_GATEWAY_BUTTON_TEXT'),
              onClick: () => onOpenUpdateDeviceDrawer(values),
            } : null,
            !values.gatewayUuid ? {
              key: 'deleteDevice',
              label: t('_DELETE_DEVICE_WITHOUT_GATEWAY_BUTTON_TEXT'),
              onClick: () => onOpenDeleteDeviceModal(values),
              danger: true,
            } : null,
          ]} />
        )} />
      },
    },
  ];
};
